/**
 * Product stock-status messaging.
 *
 * Ported from the Magento PDP template (`Magento_Catalog/.../product/view/type/default.phtml`),
 * where the `preorder` / `special_order` / `dropship` custom attributes drove the
 * availability badge + shipping message. These flags are display/messaging only —
 * actual stock is owned by ShipEdge, not by these booleans.
 *
 * Single source of truth for the badge shown on the PDP and the camera-brackets page.
 */

import type { DisplayAvailabilityState } from '@/lib/availability-types';

/** Minimal product shape needed to derive a stock status. */
export interface StockStatusInput {
  isPreorder?: boolean | null;
  isSpecialOrder?: boolean | null;
  isDropship?: boolean | null;
}

export interface ProductStockStatus {
  /** Short badge label, e.g. "In Stock". */
  label: string;
  /** Supporting shipping/availability message shown next to the badge. */
  message: string;
  /** CSS modifier appended to `.stock-badge` (e.g. "in-stock"). */
  modifier: 'in-stock' | 'special-order' | 'preorder' | 'backordered' | 'out-of-stock';
}

/**
 * Resolve the stock status for a product from its fulfillment flags plus the
 * live ShipEdge availability state (from `useAvailability` / resolveStock).
 *
 * Precedence:
 *   preorder → special-order / dropship → live backordered / unavailable → in-stock
 *
 * Flag-driven statuses win because those products bypass the live stock check
 * entirely (`isStockBypassed`), so their live state is always 'available'.
 * Callers without a live lookup (server-rendered lists) omit `liveState` and
 * get the flags-only behavior.
 */
export function getProductStockStatus(
  product: StockStatusInput,
  liveState: DisplayAvailabilityState = 'available'
): ProductStockStatus {
  if (product.isPreorder) {
    return {
      label: 'Pre Order',
      modifier: 'preorder',
      message: 'This item is in production and will be available soon.',
    };
  }

  if (product.isSpecialOrder || product.isDropship) {
    return {
      label: 'Special Order',
      modifier: 'special-order',
      message:
        'This item is available through special order. Small orders typically ship in 3-5 business days. Large orders will take additional time. Please contact us before purchasing.',
    };
  }

  if (liveState === 'backordered') {
    return {
      label: 'Backordered',
      modifier: 'backordered',
      message: 'Contact your sales rep for a delivery timeline.',
    };
  }

  if (liveState === 'unavailable') {
    return {
      label: 'Out of Stock',
      modifier: 'out-of-stock',
      message: 'This item is no longer available and cannot be ordered.',
    };
  }

  return {
    label: 'In Stock',
    modifier: 'in-stock',
    message: 'Usually ships the same business day if ordered before 2PM EST.',
  };
}
